import { useQuery } from "@tanstack/react-query";
import { berserkr, isSupabaseConfigured } from "../lib/supabase";

export interface CampaignParam { key: string; label: string; value: number | null; unit: string | null; cite: string | null; notes: string | null; }
export interface CampaignDeliverable {
  key: string; name: string; description: string | null;
  artifact_kind: string | null; status_hint: string; enabled: boolean;
  constraint_note: string | null; forged_count: number | null;
}
export interface CampaignStage {
  key: string; ordinal: number; name: string; description: string | null;
  trigger: string | null; deliverables: CampaignDeliverable[];
}
export interface Campaign {
  ruleset: { code: string; name: string; version: string | null; notes: string | null } | null;
  params: CampaignParam[];
  stages: CampaignStage[];
}

/** The case's procedural campaign: stages, deliverables, and rule-driven clocks
 *  for whatever jurisdiction ruleset the case is bound to. Fully data-driven —
 *  swapping the ruleset rows swaps the jurisdiction module. */
export function useCaseCampaign(caseId: string | undefined) {
  return useQuery<Campaign | null, Error>({
    queryKey: ["case-campaign", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!caseId) return null;
      const { data, error } = await berserkr().rpc("fetch_case_campaign", { p_case_id: caseId });
      if (error) throw new Error(error.message);
      return (data as Campaign) ?? null;
    },
  });
}
